import { createContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthQuery } from '@rideiq/auth/hooks/useAuthQuery';

import { useFlags } from '../../../contexts/LDContext';

import { emptyContact, RiderFormValues } from './types';
import { buildValidationSchema } from './validationSchema';

const ReadOnlyContext = createContext<boolean>(false);

type RiderFormProviderProps = {
  children: React.ReactNode;
  /**
   * Optional partial seed for the form's default values. Used by the edit flow to
   * hydrate saved rider data.
   *
   * Edit-flow contract for `supportSettings`: every saved entry passed in must be
   * spread as `{ ...savedSetting, selected: true }`. Toggling a pill OFF flips
   * `selected: false` (preserving `details`) rather than removing the entry, which
   * is what enables retaining detail text across toggles.
   */
  defaultValues?: Partial<RiderFormValues>;
  readOnly?: boolean;
};

export const RiderFormProvider = ({ children, defaultValues, readOnly = false }: RiderFormProviderProps) => {
  const { isAdminOrOrganizerUser } = useAuthQuery();
  const { enableApprovalFlowForSharedRider } = useFlags();
  const schema = buildValidationSchema({
    validateInvoiceGroup: isAdminOrOrganizerUser,
    useApprovalFlowV2: enableApprovalFlowForSharedRider,
  });
  const { contacts, ...rest } = defaultValues ?? {};

  const methods = useForm<RiderFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      codeWord: '',
      frontSeatAuthorized: false,
      boosterSeat: false,
      dateOfBirth: null,
      invoiceGroupId: null,
      services: [],
      supportSettings: [],
      riderNotes: '',
      locationDrafts: [],
      originalLocationIds: [],
      originalContactIds: [],
      sisImportUsed: false,
      ...rest,
      contacts: contacts && contacts.length > 0 ? contacts : [emptyContact()],
    },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  return (
    <FormProvider {...methods}>
      <ReadOnlyContext.Provider value={readOnly}>{children}</ReadOnlyContext.Provider>
    </FormProvider>
  );
};
